import { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollRevealOptions {
  /**
   * Accepted for backwards-compatibility with existing call sites, but NOT used
   * as an IntersectionObserver ratio threshold — see the note below.
   */
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

/**
 * Detects when an element enters the viewport. Returns a CALLBACK ref to attach
 * and a boolean `isVisible`.
 *
 * Two deliberate choices:
 *
 * 1. Callback ref (not a RefObject): components render `null` while CMS data
 *    loads async, then mount their real content later. A plain useRef +
 *    useEffect would attach the observer once — while the node was still null —
 *    and never re-attach, leaving content stuck at opacity:0. The callback ref
 *    (re)wires the observer whenever the node mounts.
 *
 * 2. The observer fires on ANY intersection (threshold 0), with the reveal
 *    delay coming from `rootMargin`, NOT from a ratio threshold. A ratio
 *    threshold (e.g. 0.15) breaks on elements TALLER than the viewport — e.g. a
 *    single-column card grid on mobile — because the visible ratio can never
 *    reach the threshold, so the observer never fires and the whole grid stays
 *    invisible (blank page). threshold 0 + rootMargin works at any height.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: ScrollRevealOptions = {}
) {
  const { rootMargin = '0px 0px -10% 0px', once = true } = options;
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      // Tear down any previous observer (node changed or unmounted).
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;

      // Fallback for environments without IntersectionObserver: show immediately.
      if (typeof IntersectionObserver === 'undefined') {
        setIsVisible(true);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) {
              observer.disconnect();
              observerRef.current = null;
            }
          } else if (!once) {
            setIsVisible(false);
          }
        },
        { threshold: 0, rootMargin }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin, once]
  );

  // Disconnect on unmount.
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return { ref, isVisible };
}
